import type { Lot } from '../data/mockLots';
import { clickableProps } from '../lib/a11y';
import { formatAgo } from '../lib/format';
import { revealProps } from '../lib/motion';
import { SkinFrame } from './SkinFrame';
import { SkinThumbnail } from './SkinThumbnail';

export function RecentWinCard({
  lot,
  index,
  onSelect,
}: {
  lot: Lot;
  index: number;
  onSelect?: () => void;
}) {
  const finalPrice = lot.finalPrice ?? lot.currentPrice;
  const savedPct = Math.round((1 - finalPrice / lot.marketPrice) * 100);
  const winner = lot.winner;
  const winnerLabel = winner?.username ? `@${winner.username}` : (winner?.firstName ?? 'winner');

  return (
    <article
      {...clickableProps(onSelect)}
      {...revealProps(index, {
        base: 260,
        step: 50,
        // The archive can grow to dozens of cards via "load more"; cap the
        // cascade so cards settle in under ~0.7s instead of a multi-second drip.
        max: 700,
        className: `relative grid grid-cols-[60px_1fr_auto] items-center gap-3 p-2.5 border border-edge/70 bg-bg-elev/30 ${onSelect ? 'cursor-pointer hover:border-edge-bright transition-colors' : ''}`,
      })}
    >
      <div className="absolute left-0 top-0 bottom-0 w-[2px] bg-success/80" />

      <div className="relative overflow-hidden" style={{ aspectRatio: '1 / 0.8' }}>
        {lot.imageUrl ? (
          <SkinThumbnail
            imageUrl={lot.imageUrl}
            alt={`${lot.weaponLabel} | ${lot.skinName}`}
            weapon={lot.weapon}
            palette={lot.palette}
            rarityColor={lot.rarityColor ?? null}
            size="sm"
          />
        ) : (
          <SkinFrame weapon={lot.weapon} palette={lot.palette} size="sm" showPattern={false} />
        )}
      </div>

      <div className="min-w-0">
        <p className="font-display text-[12px] leading-tight truncate">
          <span className="text-success">{winnerLabel}</span>{' '}
          <span className="text-fg-muted">won</span>{' '}
          <span className="text-fg">{lot.weaponLabel}</span>{' '}
          <span style={{ color: lot.palette.accent ?? lot.palette.to }}>| {lot.skinName}</span>
        </p>
        <p className="font-mono text-[10px] text-fg-dim mt-0.5">
          {lot.bidsCount} bids · {lot.uniqueBidders} bidders ·{' '}
          {formatAgo(lot.closedAt ?? Date.now())}
        </p>
      </div>

      <div className="text-right">
        <div className="font-mono font-bold text-success text-[14px] tabular-nums leading-none">
          ${finalPrice.toFixed(2)}
        </div>
        <div className="font-mono text-[10px] text-fg-dim mt-0.5">−{savedPct}%</div>
      </div>
    </article>
  );
}
